import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner, ProgressBar } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { useAuth } from "hooks/useAuth";
import { useTrip } from "contexts/v1/masterData/TripContext";
import { getRequest, postRequest } from "services/api";
import "./bulk-reservation-dev-tool.css";

const DEV_ROLE_NAMES = ["dev", "developer", "superadmin"];
const API_URL = "/reservation";

const guestNames = [
  "Made Dev",
  "Ketut Testing",
  "Wayan Sample",
  "Nyoman Demo",
  "Komang Trial",
  "Erick QA",
  "Sarah Mock",
  "Alex Seeder",
];

const hotels = [
  "Bintang Bisma",
  "Ubud Hotel",
  "Villa Reva",
  "Kito Villa",
  "Element Ubud",
  "Mahkota",
  "Ritz Carlton",
  "Kuber Test Hotel",
];

const drivers = [
  "Made Driver",
  "Ketut Transport",
  "Wayan Pickup",
  "Nyoman OT",
  "Putu Guide",
  "Dev Driver",
];

const transports = ["OT", "PU", "P", "D"];
const tracks = ["A", "B"];

const randomItem = (items) => {
  if (!items.length) return null;
  return items[Math.floor(Math.random() * items.length)];
};

const getArrayData = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
};

const toOption = (item, labelKey = "name") => ({
  value: item.id,
  label: item[labelKey] || item.name || item.code || item.id,
});

const BulkReservationDevTool = ({ selectedDate, paymentMethods = [], onSuccess }) => {
  const { user } = useAuth();
  const { trips, updateFilterTrip, refetchTrip } = useTrip();
  const [show, setShow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [form, setForm] = useState({
    reservation_date: selectedDate || new Date().toISOString().split("T")[0],
    total: 10,
    trip_id: "",
    agent_id: "",
    payment_method_id: "",
    status: "finished",
    track: "",
    guest_transport: "",
  });

  const canUseDevTool = useMemo(() => {
    const roles = user?.roles?.map((role) => String(role?.name || role).toLowerCase()) || [];
    return roles.some((role) => DEV_ROLE_NAMES.includes(role));
  }, [user]);

  const tripOptions = useMemo(
    () =>
      (trips?.data || []).map((trip) => ({
        value: trip.id,
        label: `${trip.name || "Trip"}${trip.start_time ? ` (${trip.start_time})` : ""}${Number.isFinite(Number(trip.remaining)) ? ` - Sisa ${trip.remaining}` : ""}`,
      })),
    [trips]
  );

  const agentOptions = useMemo(() => agents.map((agent) => toOption(agent)), [agents]);

  const paymentOptions = useMemo(
    () => (paymentMethods || []).map((method) => toOption(method)),
    [paymentMethods]
  );

  useEffect(() => {
    if (!show) return;
    setForm((prev) => ({ ...prev, reservation_date: selectedDate || prev.reservation_date }));
  }, [selectedDate, show]);

  useEffect(() => {
    if (!show || !form.reservation_date) return;
    updateFilterTrip?.("date", form.reservation_date);
    refetchTrip?.();
    // TripContext exposes non-memoized helpers, so date/show are the stable triggers here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.reservation_date, show]);

  useEffect(() => {
    if (!show || agents.length) return;
    let isMounted = true;
    setIsLoadingAgents(true);
    getRequest("/agent?limit=500")
      .then((response) => {
        if (isMounted) setAgents(getArrayData(response));
      })
      .catch(() => {
        if (isMounted) setAgents([]);
      })
      .finally(() => {
        if (isMounted) setIsLoadingAgents(false);
      });
    return () => {
      isMounted = false;
    };
  }, [agents.length, show]);

  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (!show || products.length) return;
    let isMounted = true;
    getRequest("/product?limit=500")
      .then((response) => {
        if (isMounted) setProducts(getArrayData(response));
      })
      .catch(() => {
        if (isMounted) setProducts([]);
      });
    return () => {
      isMounted = false;
    };
  }, [products.length, show]);

  if (!canUseDevTool) return null;

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildRandomBooking = (index) => {
    const tripId = form.trip_id || randomItem(tripOptions)?.value;
    const agentId = form.agent_id || randomItem(agentOptions)?.value || null;
    const paymentMethodId = form.payment_method_id || randomItem(paymentOptions)?.value || null;
    const guestName = `${randomItem(guestNames)} ${String(index + 1).padStart(2, "0")}`;
    const guestTransport = form.guest_transport || randomItem(transports);
    const hotelName = randomItem(hotels);
    const phoneSuffix = String(Date.now()).slice(-4) + String(index).padStart(2, "0");
    const pax = Math.floor(Math.random() * 5) + 1;
    
    const randomProduct = randomItem(products) || { id: 1, name: "ATV Single", price_sale: 1500000 };

    return {
      reservation_date: form.reservation_date,
      trip_id: tripId,
      agent_id: agentId,
      payment_method_id: paymentMethodId,
      status: form.status || "finished",
      track: form.track || randomItem(tracks),
      guest_transport: guestTransport,
      guest_pu_time: ["PU", "P", "D"].includes(guestTransport) ? "09:00" : "",
      guest_pu_price: ["PU", "P", "D"].includes(guestTransport) ? 50000 : 0,
      guest_pu_location: ["PU", "P", "D"].includes(guestTransport) ? hotelName : "",
      guest_pax: pax,
      guest_name: guestName,
      guest_phone: `0812${phoneSuffix}`,
      guest_nationality: hotelName,
      driver: randomItem(drivers),
      payment_note: "DEV BULK DATA",
      note: "Generated from Accounting V2 dev tool",
      activities: [
        {
          productId: randomProduct.id,
          activity: randomProduct.name,
          price: randomProduct.price_sale || randomProduct.price || 1500000,
          qty: pax,
          type: "product",
        }
      ]
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const total = Number(form.total);

    if (!form.reservation_date) {
      Swal.fire("Tanggal belum diisi", "Pilih tanggal reservation terlebih dahulu.", "warning");
      return;
    }

    if (!Number.isInteger(total) || total < 1 || total > 100) {
      Swal.fire("Jumlah tidak valid", "Jumlah data harus 1 sampai 100 agar server tetap aman.", "warning");
      return;
    }

    if (!form.trip_id && !tripOptions.length) {
      Swal.fire("Trip kosong", "Pilih tanggal yang memiliki trip, atau pilih trip secara manual.", "warning");
      return;
    }

    const confirmation = await Swal.fire({
      title: "Create bulk reservation?",
      text: `${total} data dummy akan dibuat untuk tanggal ${form.reservation_date}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, buat data",
      cancelButtonText: "Batal",
    });

    if (!confirmation.isConfirmed) return;

    setIsSubmitting(true);
    setProgress(0);
    const failedItems = [];

    try {
      for (let index = 0; index < total; index += 1) {
        const payload = buildRandomBooking(index);
        try {
          await postRequest(API_URL, payload);
        } catch (error) {
          failedItems.push({ index: index + 1, message: error.message });
        }
        setProgress(Math.round(((index + 1) / total) * 100));
      }

      if (failedItems.length) {
        const message = failedItems
          .slice(0, 5)
          .map((item) => `#${item.index}: ${item.message}`)
          .join("\n");
        await Swal.fire("Sebagian gagal", `${total - failedItems.length} berhasil, ${failedItems.length} gagal.\n${message}`, "warning");
      } else {
        await new Promise(r => setTimeout(r, 400));
        await Swal.fire("Berhasil", `${total} data reservation dummy berhasil dibuat.`, "success");
        setShow(false);
      }

      onSuccess?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectValue = (options, value) => options.find((option) => option.value === value) || null;

  return (
    <>
      <Button
        variant="outline-dark"
        size="sm"
        className="accounting-dev-bulk-button"
        onClick={() => setShow(true)}
        title="Khusus role dev untuk membuat data reservation dummy"
      >
        <i className="fe fe-database me-2"></i>
        Dev Bulk
      </Button>

      <Modal show={show} onHide={() => !isSubmitting && setShow(false)} centered size="lg" backdrop="static">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton={!isSubmitting}>
            <Modal.Title>Create Bulk Reservation Dev</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isSubmitting ? (
              <div className="upload-animation-container">
                <i className="fe fe-upload-cloud upload-icon-bounce"></i>
                <h4>Membuat Data Dummy...</h4>
                <div style={{ width: "80%", margin: "20px auto" }}>
                  <ProgressBar animated now={progress} />
                  <div className="progress-text">{progress}%</div>
                </div>
                <p className="text-muted small">Harap tunggu, jangan tutup jendela ini.</p>
              </div>
            ) : (
              <>
                <div className="accounting-dev-bulk-note">
                  Tool ini khusus data testing. Kalau field select dikosongkan, sistem akan memilih data random dari master yang tersedia.
                </div>

                <div className="row g-3">
                  <div className="col-md-4">
                    <Form.Label>Tanggal Reservation</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.reservation_date}
                      onChange={(event) => updateForm("reservation_date", event.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Jumlah Data</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="100"
                      value={form.total}
                      onChange={(event) => updateForm("total", event.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={form.status}
                      onChange={(event) => updateForm("status", event.target.value)}
                    >
                      <option value="finished">Finished</option>
                      <option value="played">Played</option>
                      <option value="confirmed">Confirmed</option>
                    </Form.Select>
                  </div>

                  <div className="col-md-6">
                    <Form.Label>Trip</Form.Label>
                    <Select
                      options={tripOptions}
                      value={selectValue(tripOptions, form.trip_id)}
                      onChange={(option) => updateForm("trip_id", option?.value || "")}
                      isClearable
                      isDisabled={trips?.loading}
                      placeholder={trips?.loading ? "Loading trip..." : "Kosongkan untuk random"}
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Label>Agent</Form.Label>
                    <Select
                      options={agentOptions}
                      value={selectValue(agentOptions, form.agent_id)}
                      onChange={(option) => updateForm("agent_id", option?.value || "")}
                      isClearable
                      isLoading={isLoadingAgents}
                      placeholder="Kosongkan untuk random / tanpa agent"
                    />
                  </div>

                  <div className="col-md-4">
                    <Form.Label>Payment Method</Form.Label>
                    <Select
                      options={paymentOptions}
                      value={selectValue(paymentOptions, form.payment_method_id)}
                      onChange={(option) => updateForm("payment_method_id", option?.value || "")}
                      isClearable
                      placeholder="Kosongkan untuk random"
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Track</Form.Label>
                    <Form.Select
                      value={form.track}
                      onChange={(event) => updateForm("track", event.target.value)}
                    >
                      <option value="">Random</option>
                      <option value="A">A Premium</option>
                      <option value="B">B Regular</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-4">
                    <Form.Label>Transport</Form.Label>
                    <Form.Select
                      value={form.guest_transport}
                      onChange={(event) => updateForm("guest_transport", event.target.value)}
                    >
                      <option value="">Random</option>
                      <option value="OT">OT</option>
                      <option value="PU">Pickup & Drop</option>
                      <option value="P">Pickup Only</option>
                      <option value="D">Drop Only</option>
                    </Form.Select>
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
          {!isSubmitting && (
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setShow(false)}>
                Batal
              </Button>
              <Button variant="dark" type="submit">
                Create Bulk Data
              </Button>
            </Modal.Footer>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default BulkReservationDevTool;
